import { useCallback, useEffect, useRef } from "react";

import { getTokenCookie } from "common/context/auth/token-cookie";
import apiURL from "common/config/api-url.config";
import { API_URL_ERROR } from "common/constants/config-error-messages.constants";
import {
  DELETE,
  GET,
  PATCH,
  POST,
  PUT,
} from "common/hooks/fetch/fetch.constants";

const { AbortController } = window;

function getFetchHook(method, baseURL = apiURL) {
  return function useFetch() {
    const abortController = useRef(null);

    useEffect(() => {
      abortController.current = new AbortController();
      return function cleanup() {
        return abortController.current?.abort();
      };
    }, []);

    const request = useCallback((endpoint, body = null, headers = null) => {
      const { signal } = abortController.current;

      const token = getTokenCookie();
      const options = {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...headers,
        },
        body: body !== null ? JSON.stringify(body) : undefined,
        signal,
      };

      if (!apiURL) {
        throw new Error(API_URL_ERROR.errorTitle);
      }

      return new Promise((resolve, reject) => {
        fetch(`${baseURL}${endpoint}`, options)
          .then((response) => {
            if (response.ok) {
              return response.text();
            }
            return reject(response);
          })
          .then((textData) => {
            const jsonData = textData ? JSON.parse(textData) : {};
            return resolve(jsonData);
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              return null;
            }
            return reject(error);
          });
      });
    }, []);

    return request;
  };
}

export const useGet = getFetchHook(GET);
export const usePost = getFetchHook(POST);
export const usePut = getFetchHook(PUT);
export const usePatch = getFetchHook(PATCH);
export const useDelete = getFetchHook(DELETE);

export default getFetchHook;
